import React, { useEffect, useRef, useState } from 'react';

/**
 * CountUp — number that animates from 0 to `end` once it enters the viewport.
 * Used by hero / stats strip. Uses requestAnimationFrame for smooth easing.
 */
const CountUp = ({ end, duration = 1800, prefix = '', suffix = '', decimals = 0, separator = '.' }) => {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    if (hasRun) return undefined;
    const node = ref.current;
    if (!node) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasRun) {
          setHasRun(true);
          const startTs = performance.now();
          const animate = (now) => {
            const elapsed = Math.min(1, (now - startTs) / duration);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - elapsed, 3);
            setValue(end * eased);
            if (elapsed < 1) requestAnimationFrame(animate);
            else setValue(end);
          };
          requestAnimationFrame(animate);
        }
      });
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [end, duration, hasRun]);

  const formatted = decimals > 0
    ? value.toFixed(decimals).replace('.', ',')
    : Math.round(value).toLocaleString('es-ES').replace(/,/g, separator);

  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
};

export default CountUp;
